import { Injectable, inject, signal } from '@angular/core'
import { AwpSdbTagsApiService, SdbTag, SdbTagCatalogItem } from '../../_services/AWP/sdbTags.service'

const BATCH_SIZE = 500

/**
 * Cache condivisa dei tag SDB.
 *
 * Le pagine chiamano ensure(frazs) una volta dopo aver caricato le righe:
 * lo store deduplica le chiavi, spezza le richieste in chunk e conserva le
 * risposte in un signal. Un frazionario può avere più tag e viene salvato
 * sempre come array completo.
 */
@Injectable({
  providedIn: 'root'
})
export class SdbTagsStore {
  private readonly api = inject(AwpSdbTagsApiService)

  readonly catalog = signal<SdbTagCatalogItem[]>([])
  private readonly byFraz = signal<Record<string, SdbTag[]>>({})
  private readonly pending = new Set<string>()
  private catalogLoading = false

  loadCatalog (): void {
    if (this.catalogLoading || this.catalog().length) {
      return
    }
    this.catalogLoading = true
    this.api.getCatalog().subscribe({
      next: (items) => {
        this.catalog.set(Array.isArray(items) ? items : [])
        this.catalogLoading = false
      },
      error: () => {
        this.catalogLoading = false
      }
    })
  }

  /** Carica solo i frazionari non ancora presenti in cache (o in volo). */
  ensure (frazs: readonly string[]): void {
    const missing = [...new Set(frazs
      .map((fraz) => String(fraz ?? '').trim())
      .filter(Boolean))]
      .filter((fraz) => !(fraz in this.byFraz()) && !this.pending.has(fraz))

    for (let index = 0; index < missing.length; index += BATCH_SIZE) {
      const chunk = missing.slice(index, index + BATCH_SIZE)
      chunk.forEach((fraz) => this.pending.add(fraz))
      this.api.getBatch(chunk).subscribe({
        next: (response) => {
          const normalized = this.normalizeResponse(chunk, response)
          this.byFraz.update((current) => ({ ...current, ...normalized }))
          chunk.forEach((fraz) => this.pending.delete(fraz))
        },
        error: () => {
          // Le chiavi tornano disponibili: un reload della pagina può ritentare.
          chunk.forEach((fraz) => this.pending.delete(fraz))
        }
      })
    }
  }

  /** Tutti i tag del frazionario, nell'ordine restituito dal backend. */
  tags (fraz: string): SdbTag[] {
    return this.byFraz()[fraz] ?? []
  }

  /** Svuota la cache (es. dopo un refresh manuale o un nuovo sync SDB). */
  invalidate (): void {
    this.byFraz.set({})
    this.catalog.set([])
    this.pending.clear()
    this.catalogLoading = false
  }

  private normalizeResponse (
    requested: string[],
    response: Record<string, SdbTag[]> | null | undefined
  ): Record<string, SdbTag[]> {
    const normalized: Record<string, SdbTag[]> = {}
    requested.forEach((fraz) => {
      normalized[fraz] = this.dedupe(response?.[fraz])
    })
    // Il backend può restituire chiavi extra (es. normalizzazioni): non perderle.
    Object.entries(response ?? {}).forEach(([fraz, tags]) => {
      if (!(fraz in normalized)) {
        normalized[fraz] = this.dedupe(tags)
      }
    })
    return normalized
  }

  private dedupe (tags: SdbTag[] | null | undefined): SdbTag[] {
    const seen = new Set<number>()
    return (Array.isArray(tags) ? tags : []).filter((tag) => {
      if (!tag || seen.has(tag.id)) {
        return false
      }
      seen.add(tag.id)
      return true
    })
  }
}
